import {
  NA,
  formatPrice,
  normalizeFields,
  splitCardFields,
  fieldText,
  normalizeSpiritItem,
  normalizeHistory,
  normalizeDidYouKnow,
  displayText,
  isVipItemType
} from "./menu"

// The five allergen groups the native search screen offers, in its order, then the diets.
// `apiName` is the snapshot's group name; the id is only used for selection state.
export const ALLERGEN_GROUPS = [
  {
    title: "PROTEINS",
    items: [
      { id: "dairy", title: "Dairy", apiName: "Dairy" },
      { id: "eggs", title: "Eggs", apiName: "Eggs" },
      { id: "pork", title: "Pork", apiName: "Pork" }
    ]
  },
  {
    title: "GRAINS / LEGUMES",
    items: [
      { id: "gluten", title: "Gluten", apiName: "Gluten" },
      { id: "soybeans", title: "Soybeans", apiName: "Soybeans" },
      { id: "sesame", title: "Sesame", apiName: "Sesame" }
    ]
  },
  {
    title: "NUTS",
    items: [
      { id: "treenuts", title: "Tree Nuts", apiName: "Tree Nuts" },
      { id: "peanuts", title: "Peanuts", apiName: "Peanuts" }
    ]
  },
  {
    title: "SEAFOOD",
    items: [
      { id: "fish", title: "Fish", apiName: "Fish" },
      { id: "crustacean", title: "Crustacean Shellfish", apiName: "Crustacean Shellfish" }
    ]
  },
  {
    title: "VEGETABLE",
    items: [{ id: "allium", title: "Allium", apiName: "Allium" }]
  },
  {
    title: "DIETS",
    items: [
      { id: "Vegan", title: "Vegan", apiName: "Vegan" },
      { id: "Vegetarian", title: "Vegetarian", apiName: "Vegetarian" }
    ]
  }
]

export const ALL_PERIODS = "ALL"

// Search results carry a "Dinner • Appetizers" tag; the pills filter on the first half.
export function mealPeriodOf(categoryTag) {
  const [first] = String(categoryTag ?? "").split("•")
  return first.trim()
}

export function mealPeriodsFor(results) {
  const periods = []
  for (const item of results) {
    const period = mealPeriodOf(item.categoryTag)
    if (period && !periods.includes(period)) periods.push(period)
  }
  return [ALL_PERIODS, ...periods]
}

function detailKind(item) {
  const type = String(item?.item_type ?? "").toLowerCase()
  const tag = String(item?.category_tag ?? "").toLowerCase()
  if (type.includes("spirit")) return "spirit"
  if (type.includes("bar menu") || tag.includes("bar menu")) return "bar"
  if (type === "wine" || tag === "wine") return "wine"
  if (isVipItemType(item?.item_type)) return "vip"
  return "food"
}

export function normalizeSearchResult(raw) {
  const { fields, cardFields } = splitCardFields(normalizeFields(raw?.data_to_display))
  const kind = detailKind(raw)

  // `key` lets the card pick the allergen line out for red highlighting.
  const lines = []
  if (kind === "spirit") {
    lines.push({ key: "region", label: "Region: ", value: displayText(raw?.origin) || NA })
    const facts = (Array.isArray(raw?.unique_facts) ? raw.unique_facts : [])
      .map((entry) => displayText(entry?.fact))
      .filter(Boolean)
      .join(", ")
    if (facts) lines.push({ key: "facts", label: "Unique Facts: ", value: facts })
  } else if (kind === "bar") {
    const field =
      fields.find((entry) => entry.name.toLowerCase() === "ingredients") ??
      fields.find((entry) =>
        ["recipe", "preparation method", "garnish", "glassware"].includes(
          entry.name.toLowerCase()
        )
      )
    if (field) {
      lines.push({ key: "field", label: `${field.name}: `, value: field.values.join(", ") })
    }
  } else if (kind !== "wine" && kind !== "vip") {
    const allergens = Array.isArray(raw?.allergance)
      ? raw.allergance.filter(Boolean).join(", ")
      : fieldText(fields, ["Allergens"], "")
    lines.push({ key: "allergens", label: "Allergens: ", value: allergens || NA })
  }

  const description =
    kind === "spirit"
      ? displayText(raw?.description)
      : displayText(raw?.menu_description ?? raw?.description)

  return {
    kind,
    id: raw?.id ?? "",
    name: displayText(raw?.name),
    price: formatPrice(kind === "spirit" ? (raw?.item_price ?? raw?.price) : raw?.price),
    imageUrl: raw?.image_url ?? "",
    thumbnailUrl: raw?.image_thumbnail_url ?? "",
    categoryTag: displayText(raw?.category_tag),
    description,
    lines,
    // Opens the same detail sheet the listing rows use.
    detail:
      kind === "spirit"
        ? normalizeSpiritItem({ ...raw, item_price: raw?.item_price ?? raw?.price })
        : {
            kind: kind === "wine" ? "wine" : kind === "bar" ? "bar" : "food",
            id: raw?.id ?? "",
            name: displayText(raw?.name),
            price: formatPrice(raw?.price),
            imageUrl: raw?.image_url ?? "",
            thumbnailUrl: raw?.image_thumbnail_url ?? "",
            displayUrl: raw?.image_display_url ?? "",
            video: raw?.video ?? "",
            typeLabel: String(raw?.item_type ?? "").replace(/_/g, " ").trim(),
            itemType: raw?.item_type ?? "",
            soundbiteUrl: raw?.soundbite_url ?? "",
            headline: description,
            fields,
            cardFields,
            itemHistory: normalizeHistory(raw?.item_history),
            didYouKnow: normalizeDidYouKnow(raw?.did_you_know)
          }
  }
}
